import { useState, useCallback, useRef } from 'react';
import {
  getPublicTokenInfo,
  connectPublicWhatsApp,
  generatePublicQrCode,
  checkPublicSessionStatus,
  validateTokenFormat,
  validatePhoneFormat,
  SESSION_STATES,
  ERROR_CODES
} from '../../../services/PublicWhatsAppServices';

const usePublicConnection = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentState, setCurrentState] = useState("");
  const [validatedData, setValidatedData] = useState(null);
  const [sessionData, setSessionData] = useState(null);

  // Refs para controle de estado
  const abortController = useRef(null);

  // Função para validar token público
  const handleTokenValidation = useCallback(async (urlToken, inputData, turnstileToken) => {
    setIsLoading(true);
    setError("");

    try {
      // Cancelar requisições anteriores
      if (abortController.current) {
        abortController.current.abort();
      }
      abortController.current = new AbortController();

      // Validar formato do token antes de fazer chamada à API
      const tokenValidation = validateTokenFormat(urlToken);
      if (!tokenValidation.valid) {
        throw new Error(tokenValidation.error);
      }

      // Validar se tokens coincidem
      if (urlToken !== inputData.token) {
        throw new Error("TOKEN_MISMATCH");
      }

      // Verificar informações do token público via API
      console.log("Validando token público:", urlToken);
      const tokenInfoResult = await getPublicTokenInfo(urlToken);

      if (tokenInfoResult.success) {
        const tokenData = tokenInfoResult.data;

        // Verificar se token ainda está válido
        if (!tokenData.valid) {
          throw new Error("INVALID_TOKEN");
        }

        if (tokenData.is_used) {
          throw new Error("TOKEN_ALREADY_USED");
        }

        // Verificar se não expirou
        const now = new Date();
        const expiresAt = new Date(tokenData.expires_at);
        if (now > expiresAt) {
          throw new Error("TOKEN_EXPIRED");
        }

        // Salvar dados validados
        setValidatedData({
          token: urlToken,
          agent_name: tokenData.agent_name,
          agent_job: tokenData.agent_job,
          agent_description: tokenData.agent_description,
          agent_image: tokenData.agent_image,
          agent_area: tokenData.agent_area,
          expires_at: tokenData.expires_at,
          turnstile_token: turnstileToken
        });

        setSessionData({
          sessionId: `pub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          validatedAt: new Date().toISOString(),
          expiresAt: tokenData.expires_at
        });

        return {
          success: true,
          data: tokenData
        };

      } else {
        throw new Error(tokenInfoResult.errorCode || tokenInfoResult.error);
      }

    } catch (error) {
      console.error("Erro na validação do token:", error);

      if (error.name === "AbortError") {
        return { success: false, error: "Operação cancelada" };
      }

      let errorMessage = "Erro inesperado na validação";
      let errorCode = "UNKNOWN_ERROR";

      // Mapear erros específicos
      switch (error.message) {
        case "TOKEN_MISMATCH":
          errorMessage = "Token inserido não confere com o token da URL";
          errorCode = "TOKEN_MISMATCH";
          break;
        case ERROR_CODES.TOKEN_NOT_FOUND:
          errorMessage = "Token não encontrado. Verifique se o link está correto.";
          errorCode = "TOKEN_NOT_FOUND";
          break;
        case ERROR_CODES.TOKEN_EXPIRED:
          errorMessage = "Token expirado. Solicite um novo link de acesso.";
          errorCode = "TOKEN_EXPIRED";
          break;
        case ERROR_CODES.TOKEN_ALREADY_USED:
          errorMessage = "Este token já foi utilizado. Solicite um novo link.";
          errorCode = "TOKEN_ALREADY_USED";
          break;
        case ERROR_CODES.RATE_LIMIT_EXCEEDED:
          errorMessage = "Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.";
          errorCode = "RATE_LIMIT_EXCEEDED";
          break;
        case "TURNSTILE_FAILED":
          errorMessage = "Validação de segurança falhou. Tente novamente.";
          errorCode = "TURNSTILE_FAILED";
          break;
        default:
          if (error.message) {
            errorMessage = error.message;
          }
          break;
      }

      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
        errorCode: errorCode
      };

    } finally {
      setIsLoading(false);
    }
  }, []);

  // Função para conectar WhatsApp público
  const handleConnectionRequest = useCallback(async (phoneNumber) => {
    setIsLoading(true);
    setError("");

    try {
      if (!validatedData?.token) {
        throw new Error("Token não validado");
      }

      // Validar formato do telefone
      const phoneValidation = validatePhoneFormat(phoneNumber);
      if (!phoneValidation.valid) {
        throw new Error(phoneValidation.error);
      }

      const formattedPhone = phoneValidation.formatted.replace(/\D/g, "");
      console.log("Conectando WhatsApp público:", { token: validatedData.token, phone: formattedPhone });

      // Chamar API para conectar WhatsApp
      const connectResult = await connectPublicWhatsApp(validatedData.token, formattedPhone);

      if (connectResult.success) {
        console.log("Conexão WhatsApp iniciada:", connectResult.data);
        
        // Atualizar dados da sessão
        setSessionData(prev => ({
          ...prev,
          sessionName: connectResult.data.session_name,
          rawPhone: connectResult.data.raw_phone,
          formattedPhone: connectResult.data.formatted_phone,
          uid: connectResult.data.uid,
          status: connectResult.data.status
        }));

        return {
          success: true,
          data: connectResult.data
        };

      } else {
        throw new Error(connectResult.error || "Erro na conexão");
      }

    } catch (error) {
      console.error("Erro ao conectar WhatsApp:", error);
      
      let errorMessage = "Erro inesperado na conexão";
      
      // Mapear erros específicos
      if (error.message.includes(ERROR_CODES.TOKEN_NOT_FOUND)) {
        errorMessage = "Token não encontrado ou expirado";
      } else if (error.message.includes(ERROR_CODES.INVALID_PHONE)) {
        errorMessage = "Formato de telefone inválido";
      } else if (error.message.includes(ERROR_CODES.RATE_LIMIT_EXCEEDED)) {
        errorMessage = "Muitas tentativas. Aguarde alguns minutos.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };

    } finally {
      setIsLoading(false);
    }
  }, [validatedData]);

  // Função para gerar QR Code público
  const handleQrCodeGeneration = useCallback(async (phoneNumber) => {
    setIsLoading(true);
    setError("");

    try {
      if (!validatedData?.token) {
        throw new Error("Token não validado");
      }

      // Validar formato do telefone
      const phoneValidation = validatePhoneFormat(phoneNumber);
      if (!phoneValidation.valid) {
        throw new Error(phoneValidation.error);
      }

      const formattedPhone = phoneValidation.formatted;
      console.log("Gerando QR Code público:", { token: validatedData.token, phone: formattedPhone });

      // Chamar API para gerar QR Code
      const qrResult = await generatePublicQrCode(validatedData.token, formattedPhone);

      if (qrResult.success) {
        console.log("QR Code gerado com sucesso");
        
        return {
          success: true,
          data: qrResult.data
        };

      } else {
        throw new Error(qrResult.error || "Erro na geração do QR Code");
      }

    } catch (error) {
      console.error("Erro ao gerar QR Code:", error);
      
      let errorMessage = "Erro inesperado na geração do QR Code";
      
      // Mapear erros específicos  
      if (error.message.includes(ERROR_CODES.TOKEN_ALREADY_USED)) {
        errorMessage = "Token já foi utilizado. Solicite um novo link.";
      } else if (error.message.includes(ERROR_CODES.INVALID_SESSION_STATE)) {
        errorMessage = "Sessão não está pronta para QR Code. Tente novamente.";
      } else if (error.message.includes(ERROR_CODES.QR_GENERATION_FAILED)) {
        errorMessage = "Falha na geração do QR Code. Tente novamente.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };

    } finally {
      setIsLoading(false);
    }
  }, [validatedData]);

  // Função para verificar status da sessão
  const handleStatusCheck = useCallback(async (phoneNumber) => {
    try {
      if (!validatedData?.token) {
        throw new Error("Token não validado");
      }

      const phoneValidation = validatePhoneFormat(phoneNumber);
      if (!phoneValidation.valid) {
        throw new Error(phoneValidation.error);
      }

      const formattedPhone = phoneValidation.formatted;
      const statusResult = await checkPublicSessionStatus(validatedData.token, formattedPhone);

      if (statusResult.success) {
        return {
          success: true,
          data: statusResult.data
        };
      } else {
        return {
          success: false,
          error: statusResult.error || "Erro ao verificar status"
        };
      }

    } catch (error) {
      console.error("Erro ao verificar status:", error);
      return {
        success: false,
        error: error.message || "Erro ao verificar status"
      };
    }
  }, [validatedData]);

  // Função para lidar com sucesso na conexão WhatsApp (callback final)
  const handleConnectionSuccess = useCallback(async (connectionData) => {
    console.log("Conexão WhatsApp finalizada com sucesso:", connectionData);
    
    // Aqui você pode adicionar lógica adicional se necessário
    // Por exemplo, salvar analytics, enviar notificações, etc.
    
    return {
      success: true,
      data: {
        connection_id: `conn_${Date.now()}`,
        status: SESSION_STATES.WORKING,
        message: "WhatsApp conectado com sucesso!",
        ...connectionData
      }
    };
  }, []);

  // Função para limpar sessão
  const clearSession = useCallback(() => {
    setValidatedData(null);
    setSessionData(null);
    setError("");
    
    // Cancelar requisições pendentes
    if (abortController.current) {
      abortController.current.abort();
    }
  }, []);

  // Função para verificar se sessão está válida
  const isSessionValid = useCallback(() => {
    if (!sessionData || !validatedData) return false;

    const now = new Date();
    const expiresAt = new Date(sessionData.expiresAt);
    
    return now < expiresAt;
  }, [sessionData, validatedData]);

  return {
    // Estados
    isLoading,
    error,
    currentState,
    validatedData,
    sessionData,

    // Setters
    setCurrentState,
    setError,

    // Funções principais
    handleTokenValidation,
    handleConnectionRequest,
    handleQrCodeGeneration,
    handleStatusCheck,
    handleConnectionSuccess,
    
    // Utilitários
    clearSession,
    isSessionValid
  };
};

export default usePublicConnection;